import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  interval,
  Observable,
  Subject,
  Subscription,
  switchMap,
  take,
  takeWhile,
} from 'rxjs';
import { DeletionJob } from '@entity/deletion-job';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class DeletionJobService {
  private static readonly POLLING_INTERVAL_MS = 3000;
  private static readonly MAX_POLLS = 200; // ~10 minutes

  private readonly baseUrl = 'api/1.0/deletion-jobs';
  private readonly jobs$ = new BehaviorSubject<DeletionJob[]>([]);
  private readonly completedSubject = new Subject<DeletionJob>();
  private readonly pollingSubscriptions = new Map<number, Subscription>();

  readonly completed$: Observable<DeletionJob> = this.completedSubject.asObservable();

  private readonly httpClient = inject(HttpClient);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);

  constructor() {
    this.loadUnacknowledgedJobs();
  }

  isDeleting(entityType: DeletionJob['entityType'], entityId: number): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      const sub = this.jobs$.subscribe((jobs) => {
        const active = jobs.some(
          (j) =>
            j.entityType === entityType &&
            j.entityId === entityId &&
            (j.status === 'Pending' || j.status === 'InProgress')
        );
        subscriber.next(active);
      });
      return () => sub.unsubscribe();
    });
  }

  startPolling(jobId: number): void {
    if (this.pollingSubscriptions.has(jobId)) {
      return;
    }

    const sub = interval(DeletionJobService.POLLING_INTERVAL_MS)
      .pipe(
        take(DeletionJobService.MAX_POLLS),
        switchMap(() => this.httpClient.get<DeletionJob>(`${this.baseUrl}/${jobId}`).pipe(catchError(() => EMPTY))),
        takeWhile((job) => job.status === 'Pending' || job.status === 'InProgress', true)
      )
      .subscribe({
        next: (job) => {
          this.updateJobInList(job);
          if (job.status === 'Completed' || job.status === 'Failed') {
            this.pollingSubscriptions.delete(jobId);
            this.showResultToast(job);
            this.completedSubject.next(job);
          }
        },
        complete: () => {
          this.pollingSubscriptions.delete(jobId);
        },
      });

    this.pollingSubscriptions.set(jobId, sub);
  }

  acknowledge(jobId: number): void {
    this.httpClient.patch(`${this.baseUrl}/${jobId}/acknowledge`, {}).subscribe(() => {
      const current = this.jobs$.value.filter((j) => j.id !== jobId);
      this.jobs$.next(current);
    });
  }

  private loadUnacknowledgedJobs(): void {
    this.httpClient.get<DeletionJob[]>(this.baseUrl).subscribe({
      next: (jobs) => {
        this.jobs$.next(jobs);

        for (const job of jobs) {
          if (job.status === 'Pending' || job.status === 'InProgress') {
            this.startPolling(job.id);
          } else if (job.status === 'Completed' || job.status === 'Failed') {
            this.showResultToast(job);
          }
        }
      },
      error: () => {
        // Silently fail if user is not authorized (non-admin)
      },
    });
  }

  private updateJobInList(job: DeletionJob): void {
    const current = this.jobs$.value;
    const index = current.findIndex((j) => j.id === job.id);
    if (index >= 0) {
      const updated = [...current];
      updated[index] = job;
      this.jobs$.next(updated);
    } else {
      this.jobs$.next([...current, job]);
    }
  }

  private showResultToast(job: DeletionJob): void {
    const i18nPrefix = job.entityType === 'Mandant' ? 'MANDANTS' : 'ANWENDERBETRIEBE';
    const dataOnlyPrefix = job.dataOnly ? 'DELETE_DATA_ONLY' : 'DELETE';
    const params = { id: job.entityId, name: job.entityName ?? '' };

    if (job.status === 'Completed') {
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant(`${i18nPrefix}.${dataOnlyPrefix}_SUCCESSFULLY`, params),
        sticky: true,
        key: 'deletion',
        data: { jobId: job.id },
      });
    } else if (job.status === 'Failed') {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(`${i18nPrefix}.${dataOnlyPrefix}_FAILED`, params),
        detail: job.error,
        sticky: true,
        key: 'deletion',
        data: { jobId: job.id },
      });
    }
  }
}
